/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
/**
 * @fileoverview Company Catalog logic module.
 * Handles company catalog operations including fetching, filtering,
 * pagination, CSV export, and permission-based data retrieval.
 * @module logic/companyCatalog
 */

import i18n from "../../i18n.js";
import { refreshToken, signOut } from "../../utils/common.js";
import {
  apiMessageOrGeneric,
} from "../../utils/genericErrorMessages.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { calculateProgressStep } from "../companyEdit/index.js";

export const PERCENTAGE_STEP = 25;
export const DEFAULT_RESULTS_LIMIT = 10;
const MIN_COMPANY_SIZE = 1;
const MAX_COMPANY_SIZE = 5;

const DATE_PART_PAD_LENGTH = 2;
const DATE_PART_PAD_CHAR = "0";
const CSV_INJECTION_PREFIX = /^[=+\-@\t\r|]/;
const companyCatalogCsvKey = (suffix) =>
  `exports.companyCatalogCsv.${suffix}`;

const getCompaniesCsvHeaders = () => [
  i18n.t(companyCatalogCsvKey("size")),
  i18n.t(companyCatalogCsvKey("tradeName")),
  i18n.t(companyCatalogCsvKey("memberNumber")),
  i18n.t(companyCatalogCsvKey("rfc")),
  i18n.t(companyCatalogCsvKey("active")),
  i18n.t(companyCatalogCsvKey("profileCompletion")),
  i18n.t(companyCatalogCsvKey("primaryContact")),
  i18n.t(companyCatalogCsvKey("createRequirements")),
];

/**
 * Fetches user permissions and companies data
 * @param {Function} changePermissions - Function to update permissions in context
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object>} Object containing companies data and last key
 */
export const getData = async (changePermissions, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const userId = localStorage.getItem("userId");
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();

    if (roleData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return { items: [], lastKey: null };
      }
      return await getData(changePermissions, navigate);
    }

    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
    }
    return await getCompaniesFromDynamoDB(navigate);
  } catch (error) {
    logger.error("Error fetching data:", { err: sanitizeError(error) });
    return { items: [], lastKey: null };
  }
};

/**
 * Fetches companies from the API
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object>} Object containing companies items and last key
 */
export const getCompaniesFromDynamoDB = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return {
          items: [],
          lastKey: null,
        };
      }
      return await getCompaniesFromDynamoDB(navigate);
    }

    if (!response.ok || !Array.isArray(data.items)) {
      return {
        items: [],
        lastKey: null,
      };
    }

    return {
      items: data.items,
      lastKey: data.lastEvaluatedKey,
    };
  } catch (error) {
    logger.error("Error fetching companies:", { err: sanitizeError(error) });
    return {
      items: [],
      lastKey: null,
    };
  }
};

/**
 * Fetches more companies using pagination
 * @param {string} lastKey - Last evaluated key for pagination
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object>} Object containing additional companies and new last key
 */
export const fetchMoreCompaniesFromDynamoDB = async (lastKey, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const url = `${process.env.REACT_APP_ROOT_API}company${
      lastKey ? `?lastKey=${lastKey}` : ""
    }`;
    const response = await fetch(url, options);
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return {
          items: [],
          lastKey: null,
        };
      }
      return await fetchMoreCompaniesFromDynamoDB(lastKey, navigate);
    }

    if (!response.ok || !Array.isArray(data.items)) {
      return {
        items: [],
        lastKey: null,
      };
    }

    return {
      items: data.items,
      lastKey: data.lastEvaluatedKey,
    };
  } catch (error) {
    logger.error("Error fetching more companies:", {
      err: sanitizeError(error),
    });
    return {
      items: [],
      lastKey: null,
    };
  }
};

/**
 * Fetches remaining company catalog pages until DynamoDB has no lastKey.
 * Does not include the already-loaded items.
 * @param {string|null} lastKey - Cursor from the last loaded page
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Array>} Remaining company items
 */
export const fetchRemainingCompaniesFromDynamoDB = async (
  lastKey,
  navigate,
) => {
  const remaining = [];
  let cursor = lastKey;

  while (cursor) {
    const page = await fetchMoreCompaniesFromDynamoDB(cursor, navigate);
    if (Array.isArray(page.items) && page.items.length > 0) {
      remaining.push(...page.items);
    }
    if (!page.lastKey || page.lastKey === cursor) {
      break;
    }
    cursor = page.lastKey;
  }

  return remaining;
};

/**
 * Paginates companies list
 * @param {Array} companies - Array of companies to paginate
 * @param {number} currentPage - Current page number
 * @param {number} resultsLimit - Number of results per page
 * @returns {Array} Paginated companies array
 */
export const paginateCompanies = (companies, currentPage, resultsLimit) => {
  return companies.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );
};

/**
 * Updates the create requirements permissions for a company.
 * @param {string} companyId - The company ID
 * @param {boolean} createRequirementsPermissions - The new value for the switch
 * @param {Function} navigate - Navigation function (for token expiry)
 * @returns {Promise<Object>} The updated company object
 */
export const updateCompanyCreateRequirementsPermissions = async (
  companyId,
  createRequirementsPermissions,
  navigate,
) => {
  const options = {
    method: "PATCH",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify({ createRequirementsPermissions }),
  };

  const response = await fetch(
    `${process.env.REACT_APP_ROOT_API}company/${companyId}/create-requirements-permissions`,
    options,
  );
  const data = await response.json();

  if (data?.message === TOKEN_EXPIRED_MESSAGE) {
    const newTokens = await refreshToken();
    if (!newTokens) {
      await signOut();
      navigate("/");
      throw new Error(apiMessageOrGeneric(undefined, "TOKEN_EXPIRED"));
    }
    return updateCompanyCreateRequirementsPermissions(
      companyId,
      createRequirementsPermissions,
      navigate,
    );
  }

  if (!response.ok) {
    throw new Error(
      apiMessageOrGeneric(
        data?.message,
        "FAILED_TO_UPDATE_CREATE_REQUIREMENTS_PERMISSIONS",
      ),
    );
  }

  return data.company;
};

/**
 * Prefixes formula-like values so Excel/LibreOffice do not execute them.
 * @param {*} value - Cell value
 * @returns {string} Sanitized string
 */
export const sanitizeCsvCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const stringValue = String(value);
  if (CSV_INJECTION_PREFIX.test(stringValue.trimStart())) {
    return `'${stringValue}`;
  }
  return stringValue;
};

/**
 * Escapes a CSV cell (RFC 4180) after sanitizing formula injection.
 * @param {*} value - Cell value
 * @returns {string} Escaped CSV field
 */
export const escapeCsvField = (value) => {
  const sanitized = sanitizeCsvCell(value);
  if (/[",\n\r]/.test(sanitized)) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
};

/**
 * Maps a boolean-like company flag to localized yes/no labels.
 * @param {boolean} value
 * @returns {string}
 */
const toLocalizedYesNo = (value) =>
  value ? i18n.t("common.yes") : i18n.t("common.no");

/**
 * Localized label for company size, matching catalog option ids 1–5.
 * @param {number|string|null|undefined} size
 * @returns {string}
 */
export const getCompanySizeLabel = (size) => {
  if (size == null || size === "") {
    return "";
  }
  const numericSize = Number(size);
  if (
    !Number.isFinite(numericSize) ||
    numericSize < MIN_COMPANY_SIZE ||
    numericSize > MAX_COMPANY_SIZE
  ) {
    return "";
  }
  return i18n.t(
    `components.corporateIdentity.companySizeOption${numericSize}`,
  );
};

/** @deprecated Use getCompanySizeLabel */
export const getCompanySizeLabelEs = getCompanySizeLabel;

/**
 * Whether the company can create requirements, matching the catalog switch.
 * @param {Object} company
 * @returns {boolean}
 */
const canCreateRequirements = (company) =>
  (company?.crearRequerimientos ?? company?.createRequirementsPermissions) !==
  false;

/**
 * Maps a company to CSV columns in Spanish.
 * @param {Object} company
 * @returns {string[]}
 */
export const mapCompanyToCsvRow = (company = {}) => {
  const progressPercentage = calculateProgressStep(company) * PERCENTAGE_STEP;
  return [
    getCompanySizeLabel(company.tamanoDeLaEmpresa),
    company.nombreComercial ?? "",
    company.numeroSocio ?? "",
    company.RFC ?? "",
    toLocalizedYesNo(Boolean(company.activo)),
    `${progressPercentage}%`,
    company.correoElectronicoPrincipal ?? "",
    toLocalizedYesNo(canCreateRequirements(company)),
  ];
};

/**
 * Builds a Spanish CSV for the companies currently in the catalog table.
 * @param {Array<Object>} companies - Filtered/loaded companies (not only the current page)
 * @returns {string} CSV content without BOM
 */
export const generateCompaniesCsv = (companies = []) => {
  const rows = [getCompaniesCsvHeaders().map(escapeCsvField).join(",")];
  companies.forEach((company) => {
    rows.push(mapCompanyToCsvRow(company).map(escapeCsvField).join(","));
  });
  return `${rows.join("\r\n")}\r\n`;
};

/**
 * Triggers a CSV file download in the browser.
 * @param {string} csvContent
 * @param {string} filename
 * @returns {void}
 */
export const downloadCsvFile = (csvContent, filename) => {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Builds a dated filename for the companies catalog export.
 * @param {Date} [date]
 * @returns {string}
 */
export const getCompaniesCsvFilename = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(
    DATE_PART_PAD_LENGTH,
    DATE_PART_PAD_CHAR,
  );
  const day = String(date.getDate()).padStart(
    DATE_PART_PAD_LENGTH,
    DATE_PART_PAD_CHAR,
  );
  return `listado-empresas_${year}${month}${day}.csv`;
};

/**
 * Exports the companies currently listed in the catalog table as CSV.
 * @param {Array<Object>} companies
 * @returns {boolean} false when there is nothing to export
 */
export const handleExportCompaniesCsv = (companies) => {
  if (!Array.isArray(companies) || companies.length === 0) {
    return false;
  }
  downloadCsvFile(generateCompaniesCsv(companies), getCompaniesCsvFilename());
  return true;
};

export const getCompanyByRFC = async (rfc) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}company/rfc/${rfc}`,
      options,
    );
    return await response.json();
  } catch (error) {
    logger.error("Error fetching company by RFC:", {
      err: sanitizeError(error),
    });
    return null;
  }
};
